/**
 * Language & Text Type Validators — textHighlight, errorSpotting,
 * sentenceParser, categorize, trueFalseSeries, fillIn
 *
 * Split from typeStage.ts (2786 LOC god-file) — P2-S2
 */

import type { ValidationIssue } from './shared'
import { STAGE, blocker, coach, info } from './shared'
import type { Question } from '../../../../content'

// ─── Text Highlight Validation ────────────────────────────────────────────────

export function validateTextHighlight(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const segments = data.segments as unknown[] | undefined

  if (segments && Array.isArray(segments)) {
    if (segments.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_TEXT_HIGHLIGHT_TOO_FEW_SEGMENTS',
          'text_highlight should have at least 2 segments',
          {
            field: 'data.segments',
            questionId: qId,
            familyId: fId,
            severity: 40,
          },
        ),
      )
    }

    const hasCorrect = segments.some((seg) => {
      const s = seg as Record<string, unknown> | undefined
      return s && s.isCorrect === true
    })
    if (!hasCorrect) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_TEXT_HIGHLIGHT_NO_CORRECT_SEGMENT',
          'At least one segment must have isCorrect: true',
          {
            field: 'data.segments',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i] as Record<string, unknown> | undefined
      if (!seg || typeof seg !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TEXT_HIGHLIGHT_INVALID_SEGMENT',
            `segments[${i}] must be an object`,
            {
              field: `data.segments[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (typeof seg.text !== 'string') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TEXT_HIGHLIGHT_SEGMENT_NO_TEXT',
            `segments[${i}].text must be a string`,
            {
              field: `data.segments[${i}].text`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }

    return issues
  }

  // Position-based format: data.text + data.correctHighlights
  const text = data.text
  const highlights = data.correctHighlights as unknown[] | undefined

  if (typeof text !== 'string' || (text as string).trim() === '') {
    if (!segments) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_TEXT_HIGHLIGHT_NO_DATA',
          'text_highlight requires either data.text + data.correctHighlights or data.segments',
          {
            field: 'data',
            questionId: qId,
            familyId: fId,
            suggestion:
              'Add data.segments = [{id, text, isCorrect, isTappable}, ...] or data.text + data.correctHighlights',
          },
        ),
      )
    }
    return issues
  }

  if (highlights && Array.isArray(highlights)) {
    for (let i = 0; i < highlights.length; i++) {
      const h = highlights[i] as Record<string, unknown> | undefined
      if (
        !h ||
        typeof h !== 'object' ||
        typeof h.start !== 'number' ||
        typeof h.end !== 'number'
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TEXT_HIGHLIGHT_INVALID_RANGE',
            `correctHighlights[${i}] must have numeric start and end`,
            {
              field: `data.correctHighlights[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (typeof text === 'string') {
        if (
          (h.start as number) < 0 ||
          (h.end as number) > text.length ||
          (h.start as number) >= (h.end as number)
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_TEXT_HIGHLIGHT_OUT_OF_RANGE',
              `correctHighlights[${i}] range [${h.start}, ${h.end}) invalid for text length ${text.length}`,
              {
                field: `data.correctHighlights[${i}]`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  }

  return issues
}

// ─── Error Spotting Validation ────────────────────────────────────────────────

export function validateErrorSpotting(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const sentences = data.sentences as unknown[] | undefined

  if (sentences && Array.isArray(sentences)) {
    if (sentences.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_ERROR_SPOTTING_NO_ERRORS',
          'error_spotting should have at least 2 sentences',
          {
            field: 'data.sentences',
            questionId: qId,
            familyId: fId,
            severity: 40,
          },
        ),
      )
    }

    let hasError = false
    for (let i = 0; i < sentences.length; i++) {
      const sent = sentences[i] as Record<string, unknown> | undefined
      if (!sent || typeof sent !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_ERROR_SPOTTING_INVALID_ERROR',
            `sentences[${i}] must be an object with { text, hasError }`,
            {
              field: `data.sentences[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (
          typeof sent.text !== 'string' ||
          (sent.text as string).trim() === ''
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_ERROR_SPOTTING_INVALID_ERROR',
              `sentences[${i}].text must be a non-empty string`,
              {
                field: `data.sentences[${i}].text`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (typeof sent.hasError !== 'boolean') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_ERROR_SPOTTING_INVALID_ERROR',
              `sentences[${i}].hasError must be a boolean`,
              {
                field: `data.sentences[${i}].hasError`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (sent.hasError === true) {
          hasError = true
          if (
            typeof sent.correction !== 'string' ||
            (sent.correction as string).trim() === ''
          ) {
            issues.push(
              coach(
                'type',
                STAGE,
                'TYPE_ERROR_SPOTTING_INVALID_ERROR',
                `sentences[${i}] has hasError=true but no correction`,
                {
                  field: `data.sentences[${i}].correction`,
                  questionId: qId,
                  familyId: fId,
                  severity: 45,
                },
              ),
            )
          }
        }
      }
    }

    if (!hasError && sentences.length > 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_ERROR_SPOTTING_NO_ERRORS',
          'error_spotting sentences must contain at least one sentence with hasError=true',
          {
            field: 'data.sentences',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    return issues
  }

  // Canonical format: data.text + data.errors
  const text = data.text as string | undefined
  const errors = data.errors as unknown[] | undefined

  if (typeof text !== 'string' || text.trim() === '') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_ERROR_SPOTTING_NO_TEXT',
        'error_spotting requires non-empty data.text',
        {
          field: 'data.text',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (!errors || !Array.isArray(errors)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_ERROR_SPOTTING_NO_ERRORS',
        'error_spotting requires data.errors array',
        {
          field: 'data.errors',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  } else {
    if (errors.length < 1) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_ERROR_SPOTTING_NO_ERRORS',
          'error_spotting must have at least 1 error',
          {
            field: 'data.errors',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    for (let i = 0; i < errors.length; i++) {
      const err = errors[i] as Record<string, unknown> | undefined
      if (!err || typeof err !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_ERROR_SPOTTING_INVALID_ERROR',
            `errors[${i}] must be an object with { word, position, correction }`,
            {
              field: `data.errors[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (
          typeof err.word !== 'string' ||
          (err.word as string).trim() === ''
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_ERROR_SPOTTING_INVALID_ERROR',
              `errors[${i}].word must be a non-empty string`,
              {
                field: `data.errors[${i}].word`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (typeof err.correction !== 'string') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_ERROR_SPOTTING_INVALID_ERROR',
              `errors[${i}].correction must be a string`,
              {
                field: `data.errors[${i}].correction`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  }

  return issues
}

// ─── Sentence Parser Validation ───────────────────────────────────────────────

export function validateSentenceParser(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const words = data.words as unknown[] | undefined
  const labels = data.labels as unknown[] | undefined
  const correctAssignments = data.correctAssignments as
    | Record<string, unknown>
    | undefined

  if (!words || !Array.isArray(words) || words.length === 0) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_SENTENCE_PARSER_NO_WORDS',
        'sentence_parser requires non-empty data.words array',
        {
          field: 'data.words',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (!labels || !Array.isArray(labels) || labels.length === 0) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_SENTENCE_PARSER_NO_LABELS',
        'sentence_parser requires non-empty data.labels array',
        {
          field: 'data.labels',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    correctAssignments &&
    typeof correctAssignments === 'object' &&
    words &&
    labels
  ) {
    const labelSet = new Set(labels.map(String))
    for (const [wordIdx, label] of Object.entries(correctAssignments)) {
      const idx = Number(wordIdx)
      if (isNaN(idx) || idx < 0 || idx >= (words as unknown[]).length) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_SENTENCE_PARSER_INVALID_ASSIGNMENT',
            `correctAssignments key "${wordIdx}" is not a valid word index [0, ${(words as unknown[]).length - 1}]`,
            {
              field: `data.correctAssignments.${wordIdx}`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      if (typeof label !== 'string' || !labelSet.has(label as string)) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_SENTENCE_PARSER_INVALID_ASSIGNMENT',
            `correctAssignments["${wordIdx}"] value must be one of the defined labels`,
            {
              field: `data.correctAssignments.${wordIdx}`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Categorize Validation ────────────────────────────────────────────────────

export function validateCategorize(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const categories = data.categories as unknown[] | undefined
  const items = data.items as unknown[] | undefined

  if (!categories || !Array.isArray(categories)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_CATEGORIZE_TOO_FEW_CATEGORIES',
        'categorize requires data.categories array',
        {
          field: 'data.categories',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (categories.length < 2) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_CATEGORIZE_TOO_FEW_CATEGORIES',
        'categorize should have at least 2 categories',
        {
          field: 'data.categories',
          questionId: qId,
          familyId: fId,
          severity: 45,
        },
      ),
    )
  }

  if (!items || !Array.isArray(items)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_CATEGORIZE_TOO_FEW_ITEMS',
        'categorize requires data.items array',
        {
          field: 'data.items',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (items.length < 4) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_CATEGORIZE_TOO_FEW_ITEMS',
        'categorize should have at least 4 items',
        {
          field: 'data.items',
          questionId: qId,
          familyId: fId,
          severity: 40,
        },
      ),
    )
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i] as Record<string, unknown> | undefined
    if (!item || typeof item !== 'object') {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CATEGORIZE_INVALID_ITEM',
          `items[${i}] must be an object with { text, correctCategory }`,
          {
            field: `data.items[${i}]`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    } else {
      if (
        typeof item.text !== 'string' ||
        (item.text as string).trim() === ''
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_CATEGORIZE_INVALID_ITEM',
            `items[${i}].text must be a non-empty string`,
            {
              field: `data.items[${i}].text`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      const catIdx = item.correctCategory ?? item.category
      if (
        typeof catIdx !== 'number' ||
        (catIdx as number) < 0 ||
        (catIdx as number) >= categories.length
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_CATEGORIZE_INVALID_MAPPING',
            `items[${i}].correctCategory must be a valid index into categories [0, ${categories.length - 1}]`,
            {
              field: `data.items[${i}].correctCategory`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── True False Series Validation ─────────────────────────────────────────────

export function validateTrueFalseSeries(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const statements = data.statements as unknown[] | undefined
  const correctAnswers = data.correctAnswers as unknown[] | undefined

  if (!statements || !Array.isArray(statements)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_TRUE_FALSE_TOO_FEW',
        'true_false_series requires data.statements array',
        {
          field: 'data.statements',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (statements.length < 2) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_TRUE_FALSE_TOO_FEW',
        'true_false_series should have at least 2 statements',
        {
          field: 'data.statements',
          questionId: qId,
          familyId: fId,
          severity: 40,
        },
      ),
    )
  }

  const isObjectFormat =
    statements.length > 0 &&
    typeof statements[0] === 'object' &&
    statements[0] !== null &&
    'text' in (statements[0] as Record<string, unknown>)

  if (isObjectFormat) {
    for (let i = 0; i < statements.length; i++) {
      const stmt = statements[i] as Record<string, unknown> | null
      if (!stmt || typeof stmt !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TRUE_FALSE_INVALID_STATEMENT',
            `statements[${i}] must be an object with { text, isTrue }`,
            {
              field: `data.statements[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (
          typeof stmt.text !== 'string' ||
          (stmt.text as string).trim() === ''
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_TRUE_FALSE_INVALID_STATEMENT',
              `statements[${i}].text must be a non-empty string`,
              {
                field: `data.statements[${i}].text`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (typeof stmt.isTrue !== 'boolean') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_TRUE_FALSE_ANSWERS_MISMATCH',
              `statements[${i}].isTrue must be a boolean`,
              {
                field: `data.statements[${i}].isTrue`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  } else {
    for (let i = 0; i < statements.length; i++) {
      const stmt = statements[i]
      if (typeof stmt !== 'string' || (stmt as string).trim() === '') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TRUE_FALSE_INVALID_STATEMENT',
            `statements[${i}] must be a non-empty string`,
            {
              field: `data.statements[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }

    if (!correctAnswers || !Array.isArray(correctAnswers)) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_TRUE_FALSE_ANSWERS_MISMATCH',
          'true_false_series requires data.correctAnswers array',
          {
            field: 'data.correctAnswers',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    } else {
      if (correctAnswers.length !== statements.length) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TRUE_FALSE_ANSWERS_MISMATCH',
            `correctAnswers length (${correctAnswers.length}) must match statements length (${statements.length})`,
            {
              field: 'data.correctAnswers',
              expected: String(statements.length),
              actual: String(correctAnswers.length),
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      for (let i = 0; i < correctAnswers.length; i++) {
        if (typeof correctAnswers[i] !== 'boolean') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_TRUE_FALSE_INVALID_ANSWER',
              `correctAnswers[${i}] must be a boolean (true/false)`,
              {
                field: `data.correctAnswers[${i}]`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  }

  return issues
}

// ─── Fill In Validation ───────────────────────────────────────────────────────

export function validateFillIn(
  question: Question,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  if (
    question.correctAnswer !== undefined &&
    typeof question.correctAnswer === 'number' &&
    (!question.options || question.options.length === 0)
  ) {
    const data = question.data as Record<string, unknown> | undefined
    const hasAcceptedAnswers =
      data &&
      Array.isArray(data.acceptedAnswers) &&
      (data.acceptedAnswers as unknown[]).length > 0

    if (!hasAcceptedAnswers) {
      issues.push(
        info(
          'type',
          STAGE,
          'TYPE_FILL_IN_NUMERIC_ANSWER',
          'fill_in type has numeric correctAnswer but no options and no acceptedAnswers — add data.acceptedAnswers or convert correctAnswer to string',
          {
            field: 'correctAnswer',
            questionId: qId,
            familyId: fId,
            severity: 15,
            suggestion:
              'Add data.acceptedAnswers array with valid string answers, or change correctAnswer to a string',
          },
        ),
      )
    }
  }

  return issues
}

// ─── Verb Conjugation Flow Validation ─────────────────────────────────────────

export function validateVerbConjugationFlow(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const steps = data.steps as unknown[] | undefined
  const verb = data.verb as string | undefined

  if (typeof verb !== 'string' || verb.trim() === '') {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_VERB_FLOW_NO_VERB',
        'verb_conjugation_flow should have a non-empty data.verb',
        {
          field: 'data.verb',
          questionId: qId,
          familyId: fId,
          severity: 35,
        },
      ),
    )
  }

  if (!steps || !Array.isArray(steps)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_VERB_FLOW_NO_STEPS',
        'verb_conjugation_flow requires data.steps array',
        {
          field: 'data.steps',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (steps.length < 1) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_VERB_FLOW_EMPTY_STEPS',
        'verb_conjugation_flow must have at least 1 step',
        {
          field: 'data.steps',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i] as Record<string, unknown> | undefined
    if (!step || typeof step !== 'object') {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_VERB_FLOW_INVALID_STEP',
          `steps[${i}] must be an object`,
          {
            field: `data.steps[${i}]`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
      continue
    }

    if (
      typeof step.prompt !== 'string' ||
      (step.prompt as string).trim() === ''
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_VERB_FLOW_INVALID_STEP',
          `steps[${i}].prompt must be a non-empty string`,
          {
            field: `data.steps[${i}].prompt`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (step.acceptedAnswers !== undefined) {
      if (
        !Array.isArray(step.acceptedAnswers) ||
        (step.acceptedAnswers as unknown[]).length === 0
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_VERB_FLOW_INVALID_STEP',
            `steps[${i}].acceptedAnswers must be a non-empty array`,
            {
              field: `data.steps[${i}].acceptedAnswers`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Sentence Error Judge Validation ──────────────────────────────────────────

export function validateSentenceErrorJudge(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const sentences = data.sentences as unknown[] | undefined

  if (!sentences || !Array.isArray(sentences)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_SENT_ERROR_NO_SENTENCES',
        'sentence_error_judge requires data.sentences array',
        {
          field: 'data.sentences',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (sentences.length < 2) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_SENT_ERROR_TOO_FEW',
        'sentence_error_judge should have at least 2 sentences',
        {
          field: 'data.sentences',
          questionId: qId,
          familyId: fId,
          severity: 40,
        },
      ),
    )
  }

  let errorCount = 0

  for (let i = 0; i < sentences.length; i++) {
    const sent = sentences[i] as Record<string, unknown> | undefined
    if (!sent || typeof sent !== 'object') {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_SENT_ERROR_INVALID',
          `sentences[${i}] must be an object with { text, hasError }`,
          {
            field: `data.sentences[${i}]`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
      continue
    }

    if (typeof sent.text !== 'string' || (sent.text as string).trim() === '') {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_SENT_ERROR_INVALID',
          `sentences[${i}].text must be a non-empty string`,
          {
            field: `data.sentences[${i}].text`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (typeof sent.hasError !== 'boolean') {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_SENT_ERROR_INVALID',
          `sentences[${i}].hasError must be a boolean`,
          {
            field: `data.sentences[${i}].hasError`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (sent.hasError === true) {
      errorCount++
      if (
        typeof sent.correction !== 'string' ||
        (sent.correction as string).trim() === ''
      ) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_SENT_ERROR_NO_CORRECTION',
            `sentences[${i}] has hasError=true but no correction string`,
            {
              field: `data.sentences[${i}].correction`,
              questionId: qId,
              familyId: fId,
              severity: 45,
            },
          ),
        )
      }
    }
  }

  if (errorCount === 0 && sentences.length > 0) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_SENT_ERROR_NONE_MARKED',
        'sentence_error_judge must have exactly one sentence with hasError=true',
        {
          field: 'data.sentences',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  } else if (errorCount > 1) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_SENT_ERROR_MULTIPLE',
        `sentence_error_judge must have exactly 1 error sentence, found ${errorCount}`,
        {
          field: 'data.sentences',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}
